import React, { Component } from "react";
import {
  loading,
  alert,
  NavbarAdmin,
  Modal,
  Select,
  format_date,
  GET,
  POST,
  PUT,
  DELETE,
  t,
  can,
} from "../../components/CustomComponent.js";
import {
  ACTIONS,
  PERMISSION_MODULES,
  ALL_PERMISSIONS,
  modulePermissions,
  countPermission,
} from "../../components/people_option.js";
import { getProfile } from "../../utils/localStorage.js";
import "../../assets/css/property.css";
import "../../assets/css/people.css";

/* ============================================================================
 * ตั้งค่า › บทบาท  (/ad/roles)
 * เลือกบทบาทจากคอลัมน์ซ้าย แล้วตั้งค่าสิทธิ์รายโมดูล (ดู/เพิ่ม/แก้ไข/ลบ)
 * ในตารางเมทริกซ์ด้านขวา — แก้ค้างไว้ได้ ยังไม่บันทึกจะมีป้ายเตือนที่แถบล่าง
 *
 * ต่อกับ API แล้ว :
 *   GET    admin/roles        (Get — page: 0 = เอาทั้งหมด)
 *   POST   admin/roles        (Create / Duplicate)
 *   PUT    admin/roles/:uid   (Save)
 *   DELETE admin/roles/:uid   (Delete)
 * permission ที่ส่งขึ้น API เป็น array ของ "<module>.<action>" ตาม global.permission
 * บทบาทของระบบ (is_system) แก้ชื่อ/สิทธิ์และลบไม่ได้ backend กันไว้อีกชั้น
 * ========================================================================== */

const EMPTY_CREATE = { name: "", description: "", copy_from: "" };

/* สิทธิ์ชุด "ดูข้อมูลเท่านั้น" — ใช้เป็นปุ่มลัดตอนตั้งบทบาทใหม่ */
const READ_ONLY_PERMISSIONS = ALL_PERMISSIONS.filter((perm) => perm.endsWith(".read"));

const isTh = () => (localStorage.getItem("lang") || "TH") !== "EN";
const locale = () => (isTh() ? "th" : "en");

/* บทบาทที่บัญชีตัวเองสังกัดอยู่ — แก้ไม่ได้เพื่อกันการเพิ่มสิทธิ์ให้ตัวเอง
   backend กันไว้อีกชั้นที่ role.service.updateRole (แยกหน้าที่)
   super_user ไม่ได้อิงสิทธิ์จาก role จึงไม่ติดข้อจำกัดนี้ */
const isMyRole = (role) => {
  const profile = getProfile();
  if (!profile || profile.user_type === "super_user") return false;
  return !!role && role.uid === profile.role?.uid;
};

export default class RolesPermissions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      roles: [],

      search: "",
      active_uid: "",

      // ฉบับร่างของบทบาทที่เลือก — แก้ในนี้ก่อน กด "บันทึก" จึงส่งขึ้น API
      draft: { name: "", description: "", permission: [] },

      modal_create: false,
      create: { ...EMPTY_CREATE },
    };
  }

  componentDidMount() {
    loading(false);
    this.Get();
  }

  /* --------------------------------------------------------- ดึงรายการบทบาท */
  /* select_uid = บทบาทที่ต้องการให้เลือกหลังโหลดเสร็จ (เช่น ตัวที่เพิ่งสร้าง) */
  Get = async (select_uid = "") => {
    loading(true);
    const result = await GET("admin/roles", { page: 0 });
    loading(false);

    if (!result?.status) return;

    const roles = result.data || [];
    const wanted = select_uid || this.state.active_uid;
    const active_uid = roles.some((role) => role.uid === wanted) ? wanted : roles[0]?.uid || "";

    this.setState({ roles, active_uid }, this.loadDraft);
  };

  // คัดลอกค่าจากบทบาทที่เลือกมาเป็นฉบับร่าง
  loadDraft = () => {
    const role = this.activeRole();
    this.setState({
      draft: {
        name: role?.name || "",
        description: role?.description || "",
        permission: [...(role?.permission || [])],
      },
    });
  };

  activeRole = () => this.state.roles.find((role) => role.uid === this.state.active_uid);

  /* แก้บทบาทนี้ไม่ได้เมื่อ : เป็นบทบาทของระบบ / ไม่มีสิทธิ์แก้ / เป็นบทบาทของตัวเอง */
  readOnly = (role = this.activeRole()) => !!role?.is_system || !can("roles.update") || isMyRole(role);

  lockReason = (role = this.activeRole()) => {
    if (role?.is_system) return t("system_role_locked_desc");
    if (isMyRole(role)) return t("own_role_locked_desc");
    return t("no_permission_edit_role");
  };

  lockLabel = (role = this.activeRole()) => {
    if (role?.is_system) return t("system_role_readonly");
    if (isMyRole(role)) return t("own_role_readonly");
    return t("no_permission_readonly");
  };

  userCount = (uid) => this.state.roles.find((role) => role.uid === uid)?.user_count || 0;

  // มีการแก้ที่ยังไม่บันทึกหรือยัง (ชื่อ / คำอธิบาย / สิทธิ์)
  isDirty = () => {
    const role = this.activeRole();
    const { draft } = this.state;
    if (!role) return false;
    if (draft.name !== role.name || draft.description !== (role.description || "")) return true;
    if (draft.permission.length !== role.permission.length) return true;
    return draft.permission.some((perm) => !role.permission.includes(perm));
  };

  /* ------------------------------------------------------------ เลือกบทบาท */
  SelectRole = async (uid) => {
    if (uid === this.state.active_uid) return;
    if (this.isDirty() && !(await alert("", "warning", t("unsaved_changes"), t("discard_changes_confirm"), t("confirm"), t("cancel")))) return;
    this.setState({ active_uid: uid }, this.loadDraft);
  };

  /* -------------------------------------------------------- ติ๊กสิทธิ์การใช้งาน */
  setPermissions = (perms, on) => {
    if (this.readOnly()) return; // บทบาทระบบ / ไม่มีสิทธิ์แก้ / บทบาทของตัวเอง
    this.setState((prev) => {
      const next = new Set(prev.draft.permission);
      perms.forEach((perm) => (on ? next.add(perm) : next.delete(perm)));
      return { draft: { ...prev.draft, permission: [...next] } };
    });
  };

  has = (perm) => this.state.draft.permission.includes(perm);

  allOn = (perms) => perms.length > 0 && perms.every((perm) => this.has(perm));

  /* ------------------------------------------------------------ บันทึกสิทธิ์ */
  Save = async () => {
    const role = this.activeRole();
    const { draft } = this.state;
    if (!role) return;
    if (this.readOnly(role)) return alert("", "warning", t("warning"), this.lockReason(role));
    if (!draft.name.trim()) return alert("", "warning", t("warning"), t("verify_role_name"));
    if (!draft.permission.length) return alert("", "warning", t("warning"), t("verify_permission_empty"));

    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) return;

    loading(true);
    const result = await PUT("admin/roles/" + role.uid, {
      name: draft.name.trim(),
      description: draft.description.trim(),
      permission: draft.permission,
    });
    loading(false);

    if (!result?.status) return;

    await this.Get(role.uid);
    alert("", "success", t("success"), t("save_permission_done"));
  };

  Reset = async () => {
    if (!this.isDirty()) return;
    if (!(await alert("", "warning", t("reset_changes"), t("discard_changes_confirm"), t("confirm"), t("cancel")))) return;
    this.loadDraft();
  };

  /* ------------------------------------------------------- เพิ่มบทบาทใหม่ */
  OpenCreate = () => this.setState({ modal_create: true, create: { ...EMPTY_CREATE } });

  Create = async () => {
    const { create, roles } = this.state;
    const name = create.name.trim();

    if (!name) return alert("", "warning", t("warning"), t("verify_role_name"));
    if (roles.some((role) => role.name.trim() === name)) return alert("", "warning", t("warning"), t("verify_role_name_duplicate"));
    if (!(await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel")))) return;

    // ต้องมีสิทธิ์อย่างน้อย 1 ข้อ (API บังคับ) เริ่มจากดูแดชบอร์ดเป็นอย่างต่ำ
    const template =
      create.copy_from === "read_only"
        ? [...READ_ONLY_PERMISSIONS]
        : create.copy_from
          ? [...(roles.find((role) => role.uid === create.copy_from)?.permission || [])]
          : ["dashboard-admin.read"];

    loading(true);
    const result = await POST("admin/roles", {
      name,
      description: create.description.trim(),
      permission: template.length ? template : ["dashboard-admin.read"],
    });
    loading(false);

    if (!result?.status) return;

    this.setState({ modal_create: false });
    await this.Get(result.data?.uid);
    alert("", "success", t("success"), t("add_role_done"));
  };

  Duplicate = async (role) => {
    if (!(await alert("", "info", t("duplicate_role"), `${t("duplicate_role_confirm")}\n${role.name}`, t("confirm"), t("cancel")))) return;

    loading(true);
    const result = await POST("admin/roles", {
      name: `${role.name} (${t("copy_suffix")})`,
      description: role.description || "",
      permission: role.permission.length ? [...role.permission] : ["dashboard-admin.read"],
    });
    loading(false);

    if (!result?.status) return;

    await this.Get(result.data?.uid);
    alert("", "success", t("success"), t("duplicate_role_done"));
  };

  Delete = async (role) => {
    // ลบใช้สิทธิ์คนละตัวกับแก้ไข จึงเช็กแค่บทบาทของระบบ ไม่ใช้ readOnly()
    if (role.is_system) return alert("", "warning", t("warning"), t("system_role_locked"));
    if (this.userCount(role.uid) > 0) return alert("", "warning", t("warning"), t("cannot_delete_role_in_use"));
    if (!(await alert("", "warning", t("delete_item"), `${t("delete_role_desc")}\n${role.name}`, t("confirm"), t("cancel")))) return;

    loading(true);
    const result = await DELETE("admin/roles/" + role.uid, null);
    loading(false);

    if (!result?.status) return;

    this.setState({ active_uid: "" });
    await this.Get();
    alert("", "success", t("success"), t("delete_done"));
  };

  /* ============================================ render : คอลัมน์ซ้าย (บทบาท) */
  renderRail = () => {
    const { roles, search, active_uid } = this.state;
    const keyword = search.trim().toLowerCase();
    const list = roles.filter(
      (role) => !keyword || role.name.toLowerCase().includes(keyword) || (role.name_en || "").toLowerCase().includes(keyword)
    );

    return (
      <div className="prop-card prop-in" style={{ position: "sticky", top: "82px" }}>
        <div className="prop-card__head">
          <span className="prop-title text-14">
            <span className="prop-title__dot" />
            {t("roles")}
          </span>
          <span className="ppl-pill ppl-pill--ghost">
            {roles.length} {t("roles_defined")}
          </span>
        </div>

        <div className="p-3 pb-0">
          <div className="position-relative">
            <span className="icon prop-field-icon">{"\uf002"}</span>
            <input
              type="text"
              className="prop-field prop-field--icon"
              placeholder={t("search_roles")}
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
            />
          </div>
        </div>

        <div className="ppl-rail">
          {list.map((role) => {
            const granted = countPermission(role.permission);
            return (
              <button
                key={role.uid}
                type="button"
                className={`ppl-rail__item ${active_uid === role.uid ? "is-active" : ""}`}
                onClick={() => this.SelectRole(role.uid)}
              >
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div className="min-w-0">
                    <div className="ppl-rail__name text-truncate">{role.name}</div>
                    <div className="ppl-sub text-truncate">{role.name_en}</div>
                  </div>
                  {role.is_system ? (
                    <span className="ppl-pill ppl-pill--system">
                      <span className="icon text-10">{"\uf023"}</span>
                      {t("system")}
                    </span>
                  ) : (
                    <span className="ppl-pill ppl-pill--role">{t("custom")}</span>
                  )}
                </div>
                <div className="ppl-rail__meta">
                  <span>
                    <span className="icon">{"\uf0c0"}</span>
                    {this.userCount(role.uid)} {t("users_count")}
                  </span>
                  <span>
                    <span className="icon">{"\uf084"}</span>
                    {granted}/{ALL_PERMISSIONS.length}
                  </span>
                </div>
              </button>
            );
          })}

          {!list.length && <div className="text-center text-secondary text-12 py-4">{t("no_data")}</div>}
        </div>

        <div className="p-3 border-top" style={{ borderColor: "var(--ppl-line)" }}>
          {can("roles.create") && (
            <button type="button" className="prop-btn w-100" onClick={this.OpenCreate}>
              <span className="icon text-12">{"\uf067"}</span>
              {t("add_role")}
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ================================================ render : ตารางสิทธิ์ */
  renderMatrix = () => {
    const locked = this.readOnly();

    const cell = (module, action) => {
      const perm = `${module.key}.${action}`;
      if (!module.actions.includes(action)) {
        return (
          <td key={action} className="ppl-cell">
            <span className="ppl-dash" title={t("action_not_available")}>
              —
            </span>
          </td>
        );
      }
      const on = this.has(perm);
      return (
        <td key={action} className="ppl-cell">
          <span className={`ppl-check ${on ? "is-on" : ""} ${locked ? "is-locked" : ""}`}>
            <input
              type="checkbox"
              checked={on}
              disabled={locked}
              title={`${t(module.label)} · ${t(ACTIONS.find((item) => item.key === action).label)}`}
              onChange={(e) => this.setPermissions([perm], e.target.checked)}
            />
          </span>
        </td>
      );
    };

    return (
      <div className="table-responsive">
        <table className="ppl-matrix" style={{ minWidth: "640px" }}>
          <thead>
            <tr>
              <th>{t("module")}</th>
              {ACTIONS.map((action) => (
                <th key={action.key} className="text-center">
                  <span className="icon me-1 text-10">{action.icon}</span>
                  {t(action.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MODULES.map((module) => {
              const perms = modulePermissions(module);
              const allOn = this.allOn(perms);

              return (
                <tr key={module.key}>
                  <td>
                    <div className="ppl-matrix__module">
                      <span className="icon">{module.icon}</span>
                      <div className="min-w-0">
                        <div className="ppl-matrix__name text-truncate">{t(module.label)}</div>
                        <div className="ppl-matrix__key">{module.key}</div>
                      </div>
                      {!locked && (
                        <button
                          type="button"
                          className="prop-link ms-auto text-nowrap"
                          title={allOn ? t("clear_all") : t("select_all")}
                          onClick={() => this.setPermissions(perms, !allOn)}
                        >
                          <span className="icon text-10">{allOn ? "" : ""}</span>
                          {allOn ? t("clear_row") : t("select_row")}
                        </button>
                      )}
                    </div>
                  </td>
                  {ACTIONS.map((action) => cell(module, action.key))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  /* ============================================ render : คอลัมน์ขวา (สิทธิ์) */
  renderDetail = () => {
    const role = this.activeRole();
    const { draft } = this.state;

    if (!role) {
      return (
        <div className="prop-card prop-in">
          <div className="prop-empty">
            <div className="prop-empty__icon icon">{"\uf3ed"}</div>
            <div className="fw-bold text-dark mb-1">{t("no_role_selected")}</div>
            <div className="text-12">{t("no_role_selected_desc")}</div>
            {can("roles.create") && (
              <button type="button" className="prop-btn prop-btn--sm mt-3" onClick={this.OpenCreate}>
                <span className="icon text-12">{"\uf067"}</span>
                {t("add_role")}
              </button>
            )}
          </div>
        </div>
      );
    }

    const locked = this.readOnly(role);
    const dirty = this.isDirty();

    return (
      <div className="prop-card prop-in">
        {/* หัวกล่อง */}
        <div className="prop-card__head">
          <div className="min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="prop-title text-16">
                <span className="prop-title__dot" />
                {role.name}
              </span>
              {locked ? (
                <span className="ppl-pill ppl-pill--system">
                  <span className="icon text-10">{"\uf023"}</span>
                  {t("system_role")}
                </span>
              ) : (
                <span className="ppl-pill ppl-pill--role">{t("custom_role")}</span>
              )}
            </div>
            <div className="text-secondary text-12 mt-1">
              {t("updated")} {format_date(role.updated_at, "dd mmm yyyy hh:mm", locale())}
            </div>
          </div>

          <div className="d-flex gap-2">
            {can("roles.create") && (
              <button type="button" className="prop-iconbtn icon" title={t("duplicate_role")} onClick={() => this.Duplicate(role)}>
                {"\uf0c5"}
              </button>
            )}
            {can("roles.delete") && (
              <button
                type="button"
                className="prop-iconbtn icon"
                style={locked ? { opacity: 0.45 } : { borderColor: "#fecaca", color: "#b91c1c" }}
                title={locked ? t("system_role_locked") : t("delete_role")}
                onClick={() => this.Delete(role)}
              >
                {"\uf2ed"}
              </button>
            )}
          </div>
        </div>

        <div className="p-4">
          {locked && (
            <div className="ppl-note ppl-note--lock mb-3">
              <span className="icon">{"\uf023"}</span>
              <div>{this.lockReason(role)}</div>
            </div>
          )}

          {/* ชื่อ + คำอธิบายบทบาท */}
          <div className="row g-3 mb-4">
            <div className="col-md-5">
              <label className="prop-label">
                {t("role_name")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="prop-field"
                placeholder={t("role_name_placeholder")}
                value={draft.name}
                disabled={locked}
                onChange={(e) => this.setState((prev) => ({ draft: { ...prev.draft, name: e.target.value } }))}
              />
            </div>
            <div className="col-md-7">
              <label className="prop-label">{t("description")}</label>
              <input
                type="text"
                className="prop-field"
                placeholder={t("role_desc_placeholder")}
                value={draft.description}
                disabled={locked}
                onChange={(e) => this.setState((prev) => ({ draft: { ...prev.draft, description: e.target.value } }))}
              />
            </div>
          </div>

          {/* หัวข้อตารางสิทธิ์ + ปุ่มลัด */}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <div>
              <div className="fw-bold text-dark text-14">{t("permission_matrix")}</div>
              <div className="text-secondary text-12">{t("permission_matrix_desc")}</div>
            </div>
            {!locked && (
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm" onClick={() => this.setPermissions(READ_ONLY_PERMISSIONS, true)}>
                  <span className="icon text-12">{"\uf06e"}</span>
                  {t("preset_read_only")}
                </button>
                <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm" onClick={() => this.setPermissions(ALL_PERMISSIONS, true)}>
                  <span className="icon text-12">{"\uf00c"}</span>
                  {t("select_all")}
                </button>
                <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm" onClick={() => this.setPermissions(ALL_PERMISSIONS, false)}>
                  <span className="icon text-12">{"\uf00d"}</span>
                  {t("clear_all")}
                </button>
              </div>
            )}
          </div>

          {this.renderMatrix()}
        </div>

        {/* แถบบันทึก — ตรึงไว้ท้ายกล่องให้กดได้ทันทีแม้ตารางยาว */}
        <div className="ppl-savebar">
          {dirty ? (
            <span className="ppl-dirty">
              <span className="ppl-dirty__dot" />
              {t("unsaved_changes")}
            </span>
          ) : (
            <span className="text-secondary text-12 d-flex align-items-center gap-2">
              <span className="icon">{"\uf058"}</span>
              {locked ? this.lockLabel(role) : t("all_changes_saved")}
            </span>
          )}

          <div className="d-flex gap-2">
            <button type="button" className="prop-btn prop-btn--ghost" disabled={!dirty} onClick={this.Reset}>
              <span className="icon text-12">{"\uf0e2"}</span>
              {t("reset_changes")}
            </button>
            <button type="button" className="prop-btn" disabled={locked || !dirty} onClick={this.Save}>
              <span className="icon text-12">{"\uf0c7"}</span>
              {t("save_permission")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ============================================ render : modal เพิ่มบทบาท */
  renderCreateModal = () => {
    const { modal_create, create, roles } = this.state;

    return (
      <Modal
        size="md"
        show={modal_create}
        onHide={() => this.setState({ modal_create: false })}
        centered
        contentClassName="border-0 rounded-4 overflow-visible shadow-lg"
      >
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white">
          <div>
            <h5 className="fw-bold mb-0 text-dark">{t("add_new_role")}</h5>
            <div className="text-secondary text-12">{t("add_new_role_desc")}</div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4 bg-white">
          <div className="row g-3">
            <div className="col-12">
              <label className="prop-label">
                {t("role_name")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="prop-field"
                placeholder={t("role_name_placeholder")}
                value={create.name}
                onChange={(e) => this.setState((prev) => ({ create: { ...prev.create, name: e.target.value } }))}
              />
            </div>
            <div className="col-12">
              <label className="prop-label">{t("description")}</label>
              <input
                type="text"
                className="prop-field"
                placeholder={t("role_desc_placeholder")}
                value={create.description}
                onChange={(e) => this.setState((prev) => ({ create: { ...prev.create, description: e.target.value } }))}
              />
            </div>
            <div className="col-12">
              <label className="prop-label">{t("copy_permission_from")}</label>
              <Select
                className="prop-field prop-select"
                default={false}
                placeholder={t("start_from_scratch")}
                value={create.copy_from}
                options={[
                  { value: "", label: t("start_from_scratch") },
                  { value: "read_only", label: t("preset_read_only") },
                  ...roles.map((role) => ({ value: role.uid, label: role.name })),
                ]}
                onChange={(value) => this.setState((prev) => ({ create: { ...prev.create, copy_from: value } }))}
              />
              <div className="ppl-note mt-3">
                <span className="icon">{"\uf05a"}</span>
                <div>{t("copy_permission_hint")}</div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-end gap-2">
          <button type="button" className="prop-btn prop-btn--ghost" onClick={() => this.setState({ modal_create: false })}>
            {t("cancel")}
          </button>
          <button type="button" className="prop-btn" onClick={this.Create}>
            <span className="icon text-12">{"\uf0c7"}</span>
            {t("save")}
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  render() {
    return (
      <div className="w-100 min-vh-100 prop-page">
        <NavbarAdmin />
        <div className="body p-4">
          {/* หัวข้อหน้า */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 prop-in">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("roles_and_permissions")}</h3>
              <div className="text-secondary text-14">{t("manage_roles_desc")}</div>
            </div>
            {can("roles.create") && (
              <button type="button" className="prop-btn" onClick={this.OpenCreate}>
                <span className="icon text-14">{"\uf067"}</span>
                {t("add_role")}
              </button>
            )}
          </div>

          <div className="row g-3">
            <div className="col-xl-4 col-lg-5">{this.renderRail()}</div>
            <div className="col-xl-8 col-lg-7">{this.renderDetail()}</div>
          </div>
        </div>

        {this.renderCreateModal()}
      </div>
    );
  }
}
